import { useContext, useEffect, useState } from "react";
import { V3CheckBox } from "../OptionInput";
import { VerticalSpacing } from "../VerticalSpacing";
import { CSPMobileButtons, ShowMoreLabel } from "./ContentSearchPanel.styles";
import { V3IconChevronDown, V3IconHelpCircle } from "../Icons";
import { SourceTypesFilters } from "../SourceTypesFilters";
import { V3CaptionGrey48 } from "../ReusableStyledComponents/V3CaptionGrey48";
import { SelectItemsModal } from "../SelectItemsModal";
import { LeftSideMenuBottomPanel, LeftSideMenuOpen, SetLeftSideMenuOpen } from "../../layout/SideBarLayout";
import { V3Button } from "../Button";
import { V3ButtonColor } from "../Button/V3Button.types";
import { useWindowSize } from "../../hooks/useWindowSize";
import { theme } from "../../theme/theme";
import { areEqual, getFilterLimit, handleFormChange, sortItems } from "./ContentSearchPanel.utils";
import { V3Date } from "../V3Date";
import { format } from "date-fns";
import ReactDOM from "react-dom";
import { WrapperWithTooltip } from "../ReusableStyledComponents/WrapperWithTooltip";
import { V3Tooltip } from "../Tooltip";
import Link from "next/link";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { ContentSearchProps } from "../../types/content-search";
import { unionBy } from "lodash";

interface ContentSearchPanelProps extends ContentSearchProps {
    isManualBulkAdd?: boolean;
}

export const ContentSearchPanel = ({
    title,
    items,
    selectedItems,
    selectedTypes,
    handleItemChange,
    handleTypeChange,
    typeCounts,
    setItems,
    setTypes,
    onClearHandler,
    onSubmitHandler,
    onSetFieldValue,
    fromDate,
    toDate,
    showSourceTooltip,
    isManualBulkAdd = false
}: ContentSearchPanelProps) => {
    const size = useWindowSize();
    const setLeftMenuOpen = useContext(SetLeftSideMenuOpen);
    const leftMenuOpen = useContext(LeftSideMenuOpen);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [filterLimit, setFilterLimit] = useState(getFilterLimit(selectedItems.length));
    const [sortedItems, setSortedItems] = useState(sortItems(items, selectedItems, filterLimit));
    const [localItems, setLocalItems] = useState(selectedItems);
    const [localTypes, setLocalTypes] = useState(selectedTypes);
    const IS_ABOVE_SRC_LIMIT = items.length > filterLimit;
    const IS_MOBILE = (size.width && size.width < theme.v3.layout.sidebarLayout.mobile) || false;

    useEffect(() => {
        setLocalItems(selectedItems);
        setLocalTypes(selectedTypes);
    }, [selectedItems, selectedTypes, leftMenuOpen]);

    // If new sources are added, we want to update the local selection by adding them to the current items we have
    useEffect(() => {
        setSortedItems(sortItems(unionBy(sortedItems, items, "value"), selectedItems, filterLimit));
    }, [items]);

    const itemsToUse = IS_MOBILE ? localItems : selectedItems;
    const typesToUse = IS_MOBILE ? localTypes : selectedTypes;

    const BtnsPanel = () => {
        const leftSideMenuBottomPanel = useContext(LeftSideMenuBottomPanel);

        if (!leftSideMenuBottomPanel?.current) return;

        return ReactDOM.createPortal(
            <>
                <CSPMobileButtons mobileDisplay="flex">
                    <div>
                        <V3Button
                            text="Clear Filters"
                            autoWidth
                            minWidth={135}
                            color={V3ButtonColor.secondary}
                            onClick={() => {
                                onClearHandler();
                                setLeftMenuOpen(false);
                            }}
                        />
                    </div>
                    <div>
                        <V3Button
                            text="Save"
                            minWidth={135}
                            autoWidth
                            onClick={() => {
                                setTypes(localTypes);
                                setItems(localItems);
                                onSubmitHandler();
                                setLeftMenuOpen(false);
                            }}
                        />
                    </div>
                </CSPMobileButtons>
            </>,
            leftSideMenuBottomPanel.current
        );
    };

    return (
        <>
            <VerticalSpacing bottom={14}>
                <V3CaptionGrey48>
                    <strong>Date</strong>
                </V3CaptionGrey48>
            </VerticalSpacing>
            <VerticalSpacing bottom={24}>
                <V3Date
                    useRange
                    onDateSet={(start, end) => {
                        onSetFieldValue("from", start ? format(start, "yyyy-MM-dd") : start);
                        onSetFieldValue("to", end ? format(end, "yyyy-MM-dd") : end);

                        if (!IS_MOBILE) onSubmitHandler();
                    }}
                    start={fromDate}
                    end={toDate}
                />
            </VerticalSpacing>
            {!!sortedItems.length && (
                <>
                    <VerticalSpacing bottom={14}>
                        <WrapperWithTooltip>
                            <div>
                                <V3CaptionGrey48>
                                    <strong>{title}</strong>
                                </V3CaptionGrey48>
                            </div>
                            <div>
                                {showSourceTooltip && (
                                    <V3Tooltip
                                        tooltipMaxWidth={!sortedItems.length ? 170 : 200}
                                        placement="right"
                                        referenceContent={<V3IconHelpCircle />}
                                        tooltipContent={
                                            <>
                                                You can edit the Sources by going to your{" "}
                                                <Link href={APPLICATION_ROUTES.SETTINGS_SOURCES} passHref>
                                                    Source settings
                                                </Link>
                                            </>
                                        }
                                    />
                                )}
                            </div>
                        </WrapperWithTooltip>
                    </VerticalSpacing>
                    {sortedItems
                        .slice(0, filterLimit)
                        .sort((a, b) => a.label.localeCompare(b.label))
                        .map((item) => (
                            <VerticalSpacing bottom={18} key={item.value}>
                                <V3CheckBox
                                    label={item.label || ""}
                                    name={item.value}
                                    id={item.value}
                                    checked={itemsToUse.includes(item.value)}
                                    onChange={handleFormChange(handleItemChange, setLocalItems, localItems, IS_MOBILE)}
                                />
                            </VerticalSpacing>
                        ))}
                    {IS_ABOVE_SRC_LIMIT && (
                        <ShowMoreLabel onClick={() => setIsOpen(true)}>
                            <div>
                                <V3IconChevronDown />
                            </div>
                            <div>
                                <V3CaptionGrey48>
                                    <strong>{items.length - filterLimit} more</strong>
                                </V3CaptionGrey48>
                            </div>
                        </ShowMoreLabel>
                    )}
                </>
            )}
            {modalIsOpen && (
                <SelectItemsModal
                    isOpen={modalIsOpen}
                    setModalOpen={setIsOpen}
                    selectedItems={selectedItems}
                    items={items}
                    onModalSubmitClose={(selectedModalItems) => {
                        if (areEqual(selectedModalItems, selectedItems)) return;

                        const newFilterLimit = getFilterLimit(selectedModalItems.length);

                        setFilterLimit(newFilterLimit);
                        setSortedItems(sortItems(sortedItems, selectedModalItems, newFilterLimit));
                        setItems(selectedModalItems);

                        !IS_MOBILE ? onSubmitHandler() : undefined;
                    }}
                />
            )}
            <SourceTypesFilters
                selectedTypes={typesToUse}
                handleTypeChange={handleFormChange(handleTypeChange, setLocalTypes, localTypes, IS_MOBILE)}
                typeCounts={typeCounts}
                title="Content type"
                isManualBulkAdd={isManualBulkAdd}
            />
            <BtnsPanel />
        </>
    );
};
